/**
 * Hasta Uygulaması - Dashboard (Ana Ekran)
 * Ultrason yükleme ve bebek yüzü üretimi
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Baby, 
  Sparkles, 
  Upload,
  Image,
  LogOut,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  Camera,
  Share2,
  Download,
  RefreshCw,
  Move3d
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generations, setGenerations] = useState([]);
  
  // Upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);

  // Token kontrolü
  const getToken = () => localStorage.getItem('patient_token');
  
  const fetchProfile = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/patient');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/patient-app/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        // Email dogrulama kontrolu
        if (data.email_verified === false) {
          localStorage.setItem('patient_user', JSON.stringify(data));
          navigate('/patient/verify');
          return;
        }
      } else {
        localStorage.removeItem('patient_token');
        navigate('/patient');
      }
    } catch (error) {
      console.error('Profil hatası:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchGenerations = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/patient-app/generations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setGenerations(data.generations || []);
      }
    } catch (error) {
      console.error('Üretimler hatası:', error);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchGenerations();
  }, [fetchProfile, fetchGenerations]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görüntü dosyası seçin');
      return;
    }
    
    setSelectedImage(file);
    setGeneratedImage(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!selectedImage || !imagePreview) {
      toast.error('Lütfen önce bir ultrason görüntüsü seçin');
      return;
    }
    
    setGenerating(true);
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/patient-app/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ultrasound_image: imagePreview,
          pregnancy_week: user.pregnancy_week || 24
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setGeneratedImage(data.image_url);
        toast.success('Bebek yüzü başarıyla oluşturuldu! 🎉');
        fetchGenerations();
      } else {
        toast.error(data.detail || 'Üretim hatası');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('patient_token');
    localStorage.removeItem('patient_user');
    navigate('/patient');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Baby className="w-7 h-7 text-pink-500" />
            <span className="font-bold text-lg text-slate-800">BabyFace AI</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Karşılama */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800">
            Merhaba {user?.first_name}! 👋
          </h1>
          {user?.pregnancy_week && (
            <p className="text-slate-500 mt-1">
              {user.pregnancy_week}. hafta - Bebeğiniz gelişmeye devam ediyor! 🤰
            </p>
          )}
        </div>

        {/* Ana Kart - Yükleme & Üretim */}
        <Card className="mb-6 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-pink-500 to-purple-500 text-white">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Bebek Yüzü Oluştur
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Sol - Ultrason Yükleme */}
              <div>
                <p className="text-sm font-medium text-slate-600 mb-3">
                  1. Ultrason Görüntüsü Yükle
                </p>
                
                <label className="block cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
                    imagePreview 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-slate-200 hover:border-pink-300 hover:bg-pink-50'
                  }`}>
                    {imagePreview ? (
                      <div>
                        <img 
                          src={imagePreview} 
                          alt="Ultrason" 
                          className="w-full h-40 object-contain rounded-lg mb-2"
                        />
                        <p className="text-sm text-green-600 flex items-center justify-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Görüntü yüklendi
                        </p>
                      </div>
                    ) : (
                      <div className="py-6">
                        <Upload className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Tıkla veya sürükle</p>
                        <p className="text-xs text-slate-400 mt-1">JPG, PNG</p>
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Sağ - Sonuç */}
              <div>
                <p className="text-sm font-medium text-slate-600 mb-3">
                  2. Sonuç
                </p>
                
                <div className="border-2 rounded-xl p-4 bg-slate-50 min-h-[200px] flex items-center justify-center">
                  {generating ? (
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 text-pink-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Bebek yüzü oluşturuluyor...</p>
                      <p className="text-xs text-slate-400 mt-1">Bu 15-30 saniye sürebilir</p>
                    </div>
                  ) : generatedImage ? (
                    <div className="text-center">
                      <img 
                        src={generatedImage} 
                        alt="Bebek Yüzü" 
                        className="w-full h-40 object-contain rounded-lg mb-3"
                      />
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" variant="outline">
                          <Download className="w-4 h-4 mr-1" />
                          Indir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="ar360-view-btn"
                          onClick={() => {
                            sessionStorage.setItem('ar_360_image', generatedImage);
                            navigate('/patient/ar-360');
                          }}
                        >
                          <Move3d className="w-4 h-4 mr-1" />
                          360°
                        </Button>
                        <Button size="sm" variant="outline">
                          <Share2 className="w-4 h-4 mr-1" />
                          Paylas
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-400">
                      <Baby className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Bebek yüzü burada görünecek</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Oluştur Butonu */}
            <Button 
              className="w-full mt-6 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              size="lg"
              onClick={handleGenerate}
              disabled={!imagePreview || generating}
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Bebek Yüzü Oluştur
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Geçmiş Üretimler */}
        {generations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Geçmiş Üretimler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {generations.slice(0, 6).map((gen) => (
                  <div 
                    key={gen.id}
                    className="relative group rounded-lg overflow-hidden border"
                  >
                    {gen.generated_image_url ? (
                      <img 
                        src={gen.generated_image_url}
                        alt="Bebek"
                        className="w-full h-24 object-cover"
                      />
                    ) : (
                      <div className="w-full h-24 bg-slate-100 flex items-center justify-center">
                        {gen.status === 'processing' ? (
                          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                        ) : gen.status === 'failed' ? (
                          <XCircle className="w-5 h-5 text-red-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                      {new Date(gen.created_at).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                ))}
              </div>
              
              {generations.length > 6 && (
                <Button variant="ghost" className="w-full mt-3">
                  Tümünü Gör ({generations.length})
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PatientDashboard;
